import { trackConversion } from "@/lib/analytics/gtag";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PageShell } from "@/components/PageShell";
import { useCart } from "@/lib/cart";
import { formatPrice } from "@/lib/shop";
import { CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";

type OrderData = {
  order: {
    order_number: string;
    status: string;
    payment_status: string;
    purchase_type: string;
    customer_name: string;
    email: string;
    phone: string;
    delivery_address: string;
    delivery_city: string | null;
    delivery_postal_code: string | null;
    delivery_notes: string | null;
    subtotal: number;
    shipping: number;
    total: number;
    created_at: string;
    provider_paylink_url: string | null;
    estimated_delivery_at?: string | null;
  };
  items: Array<{ name: string; qty: number; purchase_type: string; options: Record<string, string>; line_total: number }>;
};

export const Route = createFileRoute("/shop/order/$number")({
  validateSearch: (search: Record<string, unknown>) => ({
    t: typeof search.t === "string" ? search.t : "",
    status: typeof search.status === "string" ? search.status : "",
  }),
  component: OrderStatusPage,
  head: () => ({
    meta: [
      { title: "Order status, N S Physiotherapy Shop" },
      { name: "description", content: "View the status of your rehabilitation equipment order." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const LABEL: Record<string, string> = { standard: "Standard Purchase", physio: "Physio Guided Purchase", mixed: "Standard and Physio Guided" };

function OrderStatusPage() {
  const { number } = Route.useParams();
  const { t } = useSearch({ from: "/shop/order/$number" });
  const { clear } = useCart();
  const [data, setData] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`/api/public/order-status?number=${encodeURIComponent(number)}&token=${encodeURIComponent(t)}`);
        const json = await res.json();
        if (!cancelled && res.ok) setData(json);
      } catch {}
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [number, t, tries]);

  const paid = data?.order.payment_status === "paid";

  // Only clear the cart once payment is confirmed, so failures can be retried.
  useEffect(() => {
    if (!paid) return;
    clear();
    trackConversion("purchase", "purchase", {
      transaction_id: data?.order.order_number,
      value: Number(data?.order.total) || undefined,
      currency: "ZAR",
      email: data?.order.email,
      phone: data?.order.phone,
      name: data?.order.customer_name,
    });
  }, [paid]);

  // Poll briefly while the gateway webhook lands.
  useEffect(() => {
    if (paid || tries > 6) return;
    const id = setTimeout(() => setTries((n) => n + 1), 3000);
    return () => clearTimeout(id);
  }, [paid, tries, data]);

  return (
    <PageShell>
      <section className="container-page py-14">
        <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-background p-8">
          {loading && !data ? (
            <div className="flex items-center gap-3 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading your order…</div>
          ) : !data ? (
            <div className="text-center">
              <XCircle className="mx-auto h-10 w-10 text-destructive" />
              <h1 className="mt-3 font-display text-2xl font-bold text-primary">Order not found</h1>
              <p className="mt-2 text-sm text-muted-foreground">This order link is invalid or has expired. Please contact us and quote {number}.</p>
            </div>
          ) : paid ? (
            <div>
              <div className="text-center">
                <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
                <h1 className="mt-3 font-display text-2xl font-bold text-primary">Payment received, thank you</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Order {data.order.order_number} is confirmed. A confirmation email is on its way to {data.order.email}.
                </p>
              </div>
              <Summary data={data} />
            </div>
          ) : (
            <div>
              <div className="text-center">
                {tries <= 6 ? (
                  <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
                ) : (
                  <XCircle className="mx-auto h-10 w-10 text-destructive" />
                )}
                <h1 className="mt-3 font-display text-2xl font-bold text-primary">
                  {tries <= 6 ? "Confirming your payment" : "Payment not completed"}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {tries <= 6
                    ? "This can take a few seconds. Please keep this page open."
                    : "We did not receive a successful payment for this order. Your cart has been kept so you can try again."}
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  {data.order.provider_paylink_url ? (
                    <a href={data.order.provider_paylink_url} className="btn-hero inline-flex h-11 items-center rounded-full px-6 text-sm font-semibold">
                      Retry payment
                    </a>
                  ) : null}
                  <button onClick={() => setTries((n) => n + 1)} className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-6 text-sm font-semibold text-primary">
                    <RefreshCw className="h-4 w-4" /> Check again
                  </button>
                </div>
              </div>
              <Summary data={data} />
            </div>
          )}
          <p className="mt-8 text-center text-sm text-muted-foreground">
            <Link to="/shop" className="text-primary underline underline-offset-2">Back to the shop</Link>
          </p>
        </div>
      </section>
    </PageShell>
  );
}

function Summary({ data }: { data: OrderData }) {
  return (
    <div className="mt-8 border-t border-border pt-6 text-sm">
      <div className="flex flex-wrap justify-between gap-2 text-muted-foreground">
        <span>Order number</span>
        <span className="font-semibold text-primary">{data.order.order_number}</span>
      </div>
      <div className="mt-1 flex flex-wrap justify-between gap-2 text-muted-foreground">
        <span>Purchase type</span>
        <span>{LABEL[data.order.purchase_type] ?? data.order.purchase_type}</span>
      </div>
      <ul className="mt-4 divide-y divide-border">
        {data.items.map((i, idx) => (
          <li key={idx} className="flex justify-between gap-4 py-2">
            <span className="text-primary">
              {i.name} × {i.qty}
              <span className="block text-xs text-muted-foreground">
                {LABEL[i.purchase_type] ?? i.purchase_type}
                {Object.entries(i.options ?? {}).length ? ` · ${Object.entries(i.options).map(([k, v]) => `${k}: ${v}`).join(", ")}` : ""}
              </span>
            </span>
            <span className="whitespace-nowrap font-medium text-primary">{formatPrice(Number(i.line_total))}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 space-y-1 border-t border-border pt-3">
        <Line label="Subtotal" value={formatPrice(Number(data.order.subtotal))} />
        <Line label="Delivery" value={Number(data.order.shipping) === 0 ? "Free" : formatPrice(Number(data.order.shipping))} />
        <Line label="Total" value={formatPrice(Number(data.order.total))} bold />
      </div>
      {data.order.estimated_delivery_at ? (
        <div className="mt-4 rounded-lg bg-secondary px-4 py-3 text-sm font-semibold text-primary">
          Expected delivery:{" "}
          {new Date(`${data.order.estimated_delivery_at}T12:00:00Z`).toLocaleDateString("en-ZA", {
            weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Africa/Johannesburg",
          })}
        </div>
      ) : null}
      <div className="mt-4 text-xs text-muted-foreground">
        Delivering to: {data.order.delivery_address}
        {data.order.delivery_city ? `, ${data.order.delivery_city}` : ""}
        {data.order.delivery_postal_code ? `, ${data.order.delivery_postal_code}` : ""}
      </div>
    </div>
  );
}

function Line({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? "text-base font-semibold text-primary" : "text-muted-foreground"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
